import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { Partner } from "../../models/Partner";
import { getRepromaterialOutputTemplate } from "../../document-templates/RepromaterialOutputTemplate";
import { activeUser } from "../../services/AuthService";
import {
  getOneDisposition,
  getOneDocument,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import "./NewDocumentOfOutputRepromaterialPage.scss";

export const NewDocumentOfOutputRepromaterialPreviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const isNew = !id || id === "new";

  const unwrapArray = (resp: any): any[] => {
    const d = resp?.data;
    if (d?.result && Array.isArray(d.result)) return d.result;
    if (Array.isArray(d)) return d;
    return [];
  };

  const buildDefaultDocument = (docId?: string): DocumentOfReceive => ({
    id: docId ?? "",
    warehouse:
      activeUser?.role?.name === RoleType.Warehouseman
        ? (cloneDeep(activeUser.warehouse) as Warehouse)
        : ({} as Warehouse),
    warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
    type: undefined,
    date: new Date(),
    time: new Date(),
    repromaterialProducts: [],
    repromaterialProductsJSON: "",
    partner: {} as Partner,
    shipper: {} as Partner,
    driverName: "",
    vehicleNumber: "",
    note: "",
    productOwner: {} as Partner,
    foreignProduct: false,
    finishedDocument: false,
    finishedDocumentFirstTime: false,
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [newDocumentOfOutput, setNewDocumentOfOutput] =
    useState<DocumentOfReceive>(() => buildDefaultDocument(isNew ? "" : id));

  const [productsDataTable, setProductsDataTable] = useState<Product[]>([]);

  useEffect(() => {
    let newProductsDataTable: any[] = [];
    if (newDocumentOfOutput.repromaterialProducts) {
      const allProducts = newDocumentOfOutput.repromaterialProducts ?? [];
      for (const productItem of allProducts) {
        const product = products.find((item) => item.id === productItem.id);
        newProductsDataTable.push({
          id: product?.id ?? "",
          type: ProductType.RawMaterial,
          name: products.find((item) => item.id === product?.id)
            ?.name as string,
          code: products.find((item) => item.id === product?.id)
            ?.name as string,
          category: product?.category as ProductCategory,
          lot: productItem?.lot ?? "",
          amount: productItem?.amount ?? "",
          measuringUnit: product?.measuringUnit ?? "",
        });
      }
    }
    setProductsDataTable(newProductsDataTable);
  }, [newDocumentOfOutput.repromaterialProducts, products]);

  const [pdfData, setPdfData] = useState<any>(null);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [whResp, partnersResp, prodResp] = await Promise.all([
          getAllWarehouses(),
          getAllPartners(),
          getAllProducts(),
        ]);

        const wh = unwrapArray(whResp) as Warehouse[];
        const pa = unwrapArray(partnersResp) as Partner[];
        const pr = unwrapArray(prodResp) as Product[];

        setWarehouses(wh);
        setPartners(pa);
        setProducts(pr);

        const resp = await getOneDocument(id as string);
        const document: any = resp?.data?.result ?? resp?.data;
        if (document) {
          if (document.date) {
            document.date = new Date(document.date);
            if (document.status === null) document.date = new Date();
          }
          if (document.time) {
            document.time = new Date(document.time);
            if (document.status === null) document.time = new Date();
          }
          if (!document.warehouseman)
            document.warehouseman = `${activeUser.firstName} ${activeUser.lastName}`;
          if (
            document.dispositionId &&
            document.status !== ReceiveStatus.Arrived
          ) {
            try {
              const oneDisposition = await getOneDisposition(
                document.dispositionId,
              );
              const allProductsJson = cloneDeep(
                oneDisposition.data.result
                  ?.repromaterialDispositionProductsJSON as string,
              );
              const allProducts = JSON.parse(allProductsJson);
              const repromaterialProducts = allProducts
                .filter(
                  (productItem: any) => parseFloat(productItem?.amount) > 0,
                )
                .map((productItem: any) => ({
                  id: productItem?.id ?? "",
                  type: ProductType.RawMaterial,
                  name:
                    pr.find((item) => item.id === productItem?.id)?.name ?? "",
                  code:
                    pr.find((item) => item.id === productItem?.id)?.name ?? "",
                  category: productItem?.category as ProductCategory,
                  lot: "",
                  amount: productItem?.amount ?? "",
                  measuringUnit: productItem?.measuringUnit ?? "",
                }));
              document.repromaterialProductsJSON = allProductsJson;
              document.repromaterialProducts = repromaterialProducts;
            } catch (err) {
              console.error("Greška pri učitavanju dispozicija:", err);
            }
          }
          setNewDocumentOfOutput(document);
          const newPdf = getRepromaterialOutputTemplate(document, pr);
          setPdfData(newPdf);
        }
      } catch (error) {
        console.error("Greška pri učitavanju podataka:", error);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Došlo je do greške pri učitavanju podataka"),
          life: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [id]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRepromaterialOutputTemplate(
      newDocumentOfOutput,
      products,
    );
    setPdfData(newPdf);
  }, [newDocumentOfOutput, products]);

  const handleChanges = () => {};

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const nameOfProduct = (rowData: any) => {
    return rowData.name;
  };

  const nameOfCategory = (rowData: any) => {
    return rowData.category.name;
  };

  if (isLoading) return <ProgressSpinner />;

  return (
    <PageLayout>
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Pregled otpremnice broj")}:`}{" "}
              {newDocumentOfOutput?.finishedDocumentNumber ??
                newDocumentOfOutput.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Sve otpremnice")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/repromaterial-output-fast`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId &&
                  newDocumentOfOutput.repromaterialProducts &&
                  newDocumentOfOutput.repromaterialProducts.length > 0 &&
                  newDocumentOfOutput.driverName !== "" &&
                  newDocumentOfOutput.vehicleNumber !== "" &&
                  newDocumentOfOutput.warehouseman !== ""
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti otpremnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId &&
                  newDocumentOfOutput.repromaterialProducts &&
                  newDocumentOfOutput.repromaterialProducts.length > 0 &&
                  newDocumentOfOutput.driverName !== "" &&
                  newDocumentOfOutput.vehicleNumber !== "" &&
                  newDocumentOfOutput.warehouseman !== ""
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati otpremnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        <div className="form-body">
          <div className="form-input-fields">
            {activeUser?.role?.name === RoleType.Warehouseman ? (
              <TextInput
                id="warehouse"
                value={activeUser?.warehouse?.name ?? ""}
                onValueChange={handleChanges}
                labelText={t("Magacin")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            ) : (
              <DropdownInput
                id="warehouse"
                value={newDocumentOfOutput?.warehouse?.id}
                onValueChange={handleChanges}
                optionValue="id"
                optionLabel="name"
                options={warehouses}
                filter={false}
                labelText={t("Magacin")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            )}
            <DateInput
              id="date"
              value={
                newDocumentOfOutput?.finishedDocumentFirstTime
                  ? (newDocumentOfOutput.date as Date)
                  : new Date()
              }
              onValueChange={handleChanges}
              labelText={t("Datum")}
              placeholderText={t("Izaberite datum")}
              icon="bi bi-calendar-check"
              maxDate={new Date()}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="warehouseman"
              value={newDocumentOfOutput.warehouseman as string}
              onValueChange={handleChanges}
              labelText={t("Magacioner")}
              placeholderText={t("Unesite ime magacionera")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <DateInput
              id="time"
              value={
                newDocumentOfOutput?.finishedDocumentFirstTime
                  ? (newDocumentOfOutput.time as Date)
                  : new Date()
              }
              onValueChange={handleChanges}
              labelText={t("Vreme")}
              placeholderText={t("Izaberite vreme")}
              timeOnly={true}
              icon="bi bi-clock"
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <div className="divider-line">
              <Divider />
            </div>
            <DropdownInput
              id="partner"
              value={newDocumentOfOutput.partner?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleChanges}
              options={partners}
              filter={true}
              labelText={t("Kupac")}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <div className="new-partner-button"></div>
            <DropdownInput
              id="shipper"
              value={newDocumentOfOutput.shipper?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleChanges}
              options={partners}
              filter={true}
              labelText={t("Prevoznik")}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="driverName"
              value={newDocumentOfOutput.driverName as string}
              onValueChange={handleChanges}
              labelText={t("Ime vozača")}
              placeholderText={t("Unesite ime vozača")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="vehicleNumber"
              value={(
                newDocumentOfOutput.vehicleNumber as string
              )?.toUpperCase()}
              onValueChange={handleChanges}
              labelText={t("Registarski broj vozila")}
              placeholderText={t("Unesite registarski broj")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="loadingCityName"
              value={newDocumentOfOutput.loadingCityName as string}
              onValueChange={handleChanges}
              labelText={`${t("Mesto istovara")}`}
              placeholderText={t("Unesite mesto istovara")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="loadingContact"
              value={newDocumentOfOutput.loadingContact as string}
              onValueChange={handleChanges}
              labelText={`${t("Kontakt na istovaru")}`}
              placeholderText={t("Unesite kontakt na istovaru")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <div className="empty-field"></div>
            <div className="data-table-container">
              <DataTable value={productsDataTable}>
                <Column
                  body={nameOfProduct}
                  header={`${t("Naziv proizvoda")}:`}
                ></Column>
                <Column
                  body={nameOfCategory}
                  header={`${t("Tip proizvoda")}:`}
                ></Column>
                <Column field="lot" header={`${t("Lot")}:`}></Column>
                <Column field="amount" header={`${t("Količina")}:`}></Column>
                <Column field="measuringUnit" header={`${t("M.J")}:`}></Column>
              </DataTable>
            </div>
            <RadioButtonInput
              id="foreignProduct"
              value={newDocumentOfOutput.foreignProduct}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleChanges}
              labelText={t("Tuđa roba na skladištenju")}
              control={control}
              errors={errors}
              disabled={true}
            />
            {newDocumentOfOutput.foreignProduct === true && (
              <DropdownInput
                id="productOwner"
                value={newDocumentOfOutput.productOwner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleChanges}
                options={partners}
                filter={true}
                labelText={`${t("Vlasnik robe")}`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            )}
            <div className="note-field">
              <TextAreaInput
                id="note"
                value={newDocumentOfOutput.note as string}
                onValueChange={handleChanges}
                rows={1}
                cols={50}
                labelText={t("Napomena")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            </div>
            <div></div>
          </div>
          <div className="documents-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Otpremnica_${newDocumentOfOutput.finishedDocumentId ?? ""}`}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
